import { useState, useRef, useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { BookingBreadcrumbs } from '@features/booking/components/BookingBreadcrumbs';
import { BookingPageLayout } from '@features/booking/layouts';
import { useBooking } from '@features/booking/state';
import { useRestaurant } from '@core/tenant/TenantProvider';
import { useCommitOrAdvance } from '@features/booking/hooks/useCommitOrAdvance';
import { ThreeDSChallengeOverlay } from '@features/booking/components/ThreeDSChallengeOverlay';
import { commitCtaKey } from '@features/booking/utils/commitMomentCopy';
import { verifyAttrs } from '@/shared/verify/core/contract';
import { capturePaymentError } from '@core/sentry';
import { useMercadoPagoSDK } from '@features/booking/hooks/useMercadoPagoSDK';
import { useStripeV2 } from '@features/booking/hooks/useStripeV2';
import { usePaymentFee } from '@features/booking/hooks/usePaymentFee';
import { encryptCardData } from '@features/booking/utils/encryptCardData';
import { env } from '@core/config/env';
import { generateRandomDocumentId } from '@shared/utils/randomDocument';
import { sanitizeHtml } from '@shared/utils/sanitize';
import { FeeBreakdownDisplay } from './FeeBreakdownDisplay';
import { PaymentAcceptances } from './PaymentAcceptances';
import { BillingSection } from './BillingSection';

function formatCardNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length >= 3) {
    return digits.slice(0, 2) + '/' + digits.slice(2, 4);
  }
  return digits;
}

export function MercadoPagoPaymentForm() {
  const { t } = useTranslation('booking');
  const { t: tErrors } = useTranslation('errors');
  const booking = useBooking();
  const restaurant = useRestaurant();
  const mpSDK = useMercadoPagoSDK();
  const stripeV2 = useStripeV2(env.stripeFallbackKey);
  const { feeBreakdown, displayCurrency, slotType, cancellationHours } = usePaymentFee();

  // T14b: Guarantee-as-commit-host wiring. See StripePaymentForm for the
  // full rationale — same pattern. The hook decides commit vs goToNext
  // based on `getNextStep()?.id === 'BOOKING_FINAL'`, computed from the
  // step set that's now flag-aware (CONFIRMATION conditional in steps.ts).
  const {
    commitOrAdvance,
    willCommit,
    nextStepPath,
    isSubmitting,
    error: commitError,
    requireReload,
    isHandling3ds,
  } = useCommitOrAdvance();

  // Ref so handleSubmit can call the LATEST commitOrAdvance after the
  // flushSync that publishes the freshly-tokenized card to bookingState.
  const commitOrAdvanceRef = useRef(commitOrAdvance);
  commitOrAdvanceRef.current = commitOrAdvance;

  const mpFormRef = useRef<HTMLFormElement>(null);
  const stripeFormRef = useRef<HTMLFormElement>(null);

  const [holderName, setHolderName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [paymentMethodId, setPaymentMethodId] = useState('');

  const [allAccepted, setAllAccepted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const feeAmount = feeBreakdown.total;
  // Only gate on MercadoPago readiness. Stripe v2 is best-effort for dual-tokenization:
  // if it fails, the backend accepts cardTokenStripe='' and handles service fees without it.
  // Gating on both would block the entire payment if Stripe CDN is slow/down.
  const sdksReady = mpSDK.isReady;

  // Random doc number (matches legacy behavior)
  const docNumberRef = useRef(
    generateRandomDocumentId().toString()
  );

  // Detect payment method on card number change
  useEffect(() => {
    const digits = cardNumber.replace(/\D/g, '');
    const bin = digits.slice(0, 6);
    if (bin.length >= 6 && mpSDK.isReady) {
      mpSDK.getPaymentMethod(bin)
        .then(setPaymentMethodId)
        .catch(() => { /* non-critical */ });
    }
  }, [cardNumber, mpSDK.isReady, mpSDK.getPaymentMethod]);

  const syncHiddenForms = useCallback(() => {
    const digits = cardNumber.replace(/\D/g, '');
    const parts = expiry.split('/');
    const month = parts[0] || '';
    const year = parts[1] || '';

    // Sync MercadoPago hidden form
    if (mpFormRef.current) {
      const setVal = (id: string, val: string) => {
        const el = mpFormRef.current!.querySelector(`#mp-${id}`) as HTMLInputElement;
        if (el) el.value = val;
      };
      setVal('cardholderName', holderName);
      setVal('cardNumber', digits);
      setVal('securityCode', cvc);
      setVal('cardExpirationMonth', month);
      setVal('cardExpirationYear', year);
      setVal('docType', 'Otro');
      setVal('docNumber', docNumberRef.current);
    }

    // Sync Stripe v2 hidden form
    if (stripeFormRef.current) {
      const setVal = (id: string, val: string) => {
        const el = stripeFormRef.current!.querySelector(`#sv2-${id}`) as HTMLInputElement;
        if (el) el.value = val;
      };
      setVal('name', holderName);
      setVal('number', digits);
      setVal('exp-month', month);
      setVal('exp-year', year);
      setVal('cvc', cvc);
    }
  }, [holderName, cardNumber, expiry, cvc]);

  const handleSubmit = async () => {
    if (!sdksReady || !allAccepted) return;

    // Capture commit intent at click time, BEFORE the flushSync below
    // mutates booking.cardToken. Without this snapshot the post-flushSync
    // re-render filters GUARANTEE out of `activeSteps` (its `isRequired`
    // depends on `!hasCardToken`), `getNextStep()` returns null, and the
    // dispatch silently no-ops. Same bug as Stripe form — see
    // useCommitOrAdvance interface JSDoc for the full mechanism.
    const capturedIntent = { willCommit, nextStepPath };

    setIsProcessing(true);
    setError(null);

    // Validate inputs
    const digits = cardNumber.replace(/\D/g, '');
    if (!holderName.trim()) {
      setError(t('FORM_ERROR_CREDIT_CARD_NAME'));
      setIsProcessing(false);
      return;
    }
    if (digits.length < 13 || digits.length > 19) {
      setError(t('FORM_ERROR_CREDIT_CARD'));
      setIsProcessing(false);
      return;
    }
    const expiryRegex = /^(0[1-9]|1[0-2])\/(\d{2})$/;
    if (!expiryRegex.test(expiry)) {
      setError(t('FORM_ERROR_CREDIT_CARD_EXPIRES'));
      setIsProcessing(false);
      return;
    }
    if (cvc.length < 3 || cvc.length > 4) {
      setError(t('FORM_ERROR_SECURITY_CODE'));
      setIsProcessing(false);
      return;
    }

    // feeBreakdown applies the menu→slot guarantee hierarchy; slotType does
    // not. The GUARANTEE step requires a payment-bearing guarantee, so a
    // null type here is an invariant violation, not a fallback case.
    const resolvedGuaranteeType = feeBreakdown.guaranteeType;
    if (resolvedGuaranteeType !== 'deposit' && resolvedGuaranteeType !== 'no_show') {
      Sentry.captureException(
        new Error('Payment submit reached without resolved guarantee type'),
        {
          extra: {
            slotType,
            feeBreakdownGuaranteeType: feeBreakdown.guaranteeType,
            feeBreakdownTotal: feeBreakdown.total,
            restaurantId: restaurant.id,
            paymentProcessor: 'mercadopago',
          },
        },
      );
      setError(t('TRADITIONAL_GRAB_ERROR'));
      setIsProcessing(false);
      return;
    }

    // Check AMEX restriction
    const firstTwo = digits.slice(0, 2);
    const isAmex = firstTwo === '34' || firstTwo === '37';
    if (isAmex && !restaurant.allowAMEXBR && resolvedGuaranteeType === 'deposit') {
      setError(t('STRIPE_BRAZIL_AMEX_ERROR'));
      setIsProcessing(false);
      return;
    }

    // Sync hidden forms before tokenization
    syncHiddenForms();

    try {
      // Step 1: Create Stripe v2 token (best-effort)
      let cardTokenStripe = '';
      try {
        const stripeResponse = await stripeV2.createToken(stripeFormRef.current!);
        cardTokenStripe = stripeResponse.id || '';

        // Check debit card restriction (Brazil only) — uses Stripe response when available
        if (stripeResponse.card?.funding === 'debit' && restaurant.defaultLanguage === 'pt') {
          setError(t('STRIPE_BRAZIL_DEBIT_CARD_ERROR'));
          setIsProcessing(false);
          return;
        }
      } catch {
        // Stripe v2 token is best-effort for MercadoPago flow
      }

      // Step 2: Create MercadoPago token (primary)
      const mpToken = await mpSDK.createToken(mpFormRef.current!);

      // Step 3: Encrypt card data
      const parts = expiry.split('/');
      const encrypted = encryptCardData({
        number: digits,
        expiryMonth: parts[0],
        expiryYear: parts[1],
        cvc,
        holderName,
      });

      // Step 4: Save everything to booking state.
      // flushSync so the next render publishes cardToken + encrypted fields
      // before commitOrAdvance reads booking. When this form is the commit
      // host, the commit would otherwise POST without the just-minted MP
      // token. Same pattern as StripePaymentForm and PersonalizePage.
      flushSync(() => {
        booking.updateBooking({
          cardToken: mpToken,
          cardTokenStripe,
          paymentMethod: paymentMethodId || null,
          paymentProcessor: 'mercadopago',
          feeDeposit: feeAmount,
          feeGratuity: feeBreakdown.gratuity,
          guaranteeType: resolvedGuaranteeType,
          encryptedCardNumber: encrypted.temp1,
          encryptedExpiry: encrypted.temp3,
          encryptedCvc: encrypted.temp4,
          encryptedHolderName: encrypted.temp6,
        });
      });

      // Post-flushSync: pass the click-time `willCommit` snapshot so
      // dispatch doesn't get fooled by the GUARANTEE-is-now-filtered
      // re-render (see capture comment at top of handleSubmit).
      await commitOrAdvanceRef.current(capturedIntent);
    } catch (err) {
      // User-visible: keep the translated fallback — surfacing raw
      // `err.message` would leak English SDK strings or stack-y dev
      // text into a Spanish UI. Diagnostic value belongs in Sentry +
      // DEV console, not the inline error block. (Stripe's path goes
      // through `getStripeErrorMessage` for the same reason.)
      setError(t('TRADITIONAL_GRAB_ERROR'));
      // Route the exception through the canonical helper so it lands
      // on the same Sentry dashboards as every other payment-commit
      // error: dotted `payment.processor` tag, ApiError-aware
      // fingerprinting, `isExpectedClientTransportError` filter for
      // dropped-network noise. Replacing a previous inline
      // `Sentry.captureException` that bypassed all of that.
      capturePaymentError(err, 'mercadopago', {
        restaurantId: restaurant.id,
        paymentMethodId,
        stage: 'tokenization-or-commit',
      });
      // DEV-only console.error so the live diagnostic appears in the dev
      // tools immediately. Stripped from prod builds by Vite tree-shake.
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.error('[MercadoPagoPaymentForm] handleSubmit failed:', err);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <BookingPageLayout
      title={t('GRAB_CONFIRMATION_TITLE_4')}
      subtitle={t('GRAB_CONFIRMATION_TITLE_7') || undefined}
      subtitleClassName="text-foreground/70 text-lg mb-6 text-center"
      breadcrumbs={<BookingBreadcrumbs />}
    >
      <div
        className="w-full sm:px-6 space-y-6"
        {...verifyAttrs("MercadoPagoPaymentForm", {
          willCommit,
          hostStep: "guarantee",
          // T30 D1: MP renders only for AR/MX tenants on deposit/no_show
          // guarantees with non-zero fees. hasFee is effectively always
          // true in production, but the helper flips to GUARANTEE_NO_FEE
          // defensively if the BE ever waives.
          hasFee: feeBreakdown.total > 0,
          hasError: !!commitError,
          isSubmitting,
          isProcessing,
          requireReload,
          ctaKey: requireReload
            ? "PAYMENT_3DS_RELOAD_TO_RETRY"
            : isProcessing
              ? "PAYMENT_PROCESSING"
              : commitCtaKey({
                  hostStep: "guarantee",
                  // hasFee=true ONLY for deposit (immediate charge).
                  // no_show is a hold — "You won't be charged at this
                  // moment" — so the CTA should not promise payment.
                  // See StripePaymentForm for the matching rationale.
                  hasFee: feeBreakdown.guaranteeType === 'deposit' && feeBreakdown.total > 0,
                  isSubmitting,
                  hasError: !!commitError,
                  isWaitingList: booking.flowType === 'waiting_list',
                }),
        })}
      >
        {booking.flowType === 'waiting_list' ? (
          <div className="text-foreground/80 text-sm space-y-2">
            <p>{t('ADD_CREDIT_CARD_TO_JOIN_WAITING_LIST_1')}</p>
            <p>{t('ADD_CREDIT_CARD_TO_JOIN_WAITING_LIST_2')}</p>
            <p>{t('NO_SHOW_GUARANTEE_APPLICABLE_1')}</p>
            <p>{t('NO_SHOW_GUARANTEE_APPLICABLE_2')}</p>
          </div>
        ) : (
          <div className="text-center text-foreground/70 text-sm">
            <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(t('NEW_MODAL_PROVIDE_CC_DETAILS')) }} />
            <p>{t('STRING_PAYMENT_CREDITED')}</p>
          </div>
        )}

        {/* SDK loading state */}
        {!sdksReady && (
          <div className="text-center text-foreground/50 text-sm py-4">
            {mpSDK.error ? t('PAYMENT_SDK_LOAD_ERROR') : t('PAYMENT_LOADING')}
          </div>
        )}

        {/* Card input fields (visible) */}
        {sdksReady && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                {t('PAYMENT_LABEL_CC_2')}
              </label>
              <input
                data-testid="card-holder"
                type="text"
                className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                value={holderName}
                onChange={(e) => {
                  setHolderName(e.target.value);
                  // Clear the "Cardholder name is required" submit error the
                  // moment the user starts typing (same pattern as Stripe
                  // form). MP form has several other field validations that
                  // set the same `error` state, so we only clear when this
                  // particular field transitions to non-empty.
                  if (e.target.value.trim() && error) setError(null);
                }}
                placeholder="John Doe"
              />
            </div>

            <BillingSection />

            <div>
              <label className="block text-sm font-medium text-foreground/70 mb-1">
                {t('PAYMENT_LABEL_CC')}
              </label>
              <input
                data-testid="card-number"
                type="text"
                className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                value={cardNumber}
                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                placeholder="4242 4242 4242 4242"
                maxLength={23}
                inputMode="numeric"
              />
            </div>

            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground/70 mb-1">
                  {t('STRING_PAYMENT_EXPIRES')}
                </label>
                <input
                  data-testid="card-expiry"
                  type="text"
                  className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  placeholder="MM/YY"
                  maxLength={5}
                  inputMode="numeric"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-foreground/70 mb-1">
                  {t('STRING_SECURITY_CODE')}
                </label>
                <input
                  data-testid="card-cvc"
                  type="text"
                  className="w-full border border-border rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand/20 focus:border-brand"
                  value={cvc}
                  onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="123"
                  maxLength={4}
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>
        )}

        {/* Hidden MercadoPago form */}
        <form ref={mpFormRef} name="mercadoPagoForm" style={{ display: 'none' }}>
          <input id="mp-cardholderName" data-checkout="cardholderName" type="hidden" />
          <input id="mp-cardNumber" data-checkout="cardNumber" type="hidden" />
          <input id="mp-securityCode" data-checkout="securityCode" type="hidden" />
          <input id="mp-cardExpirationMonth" data-checkout="cardExpirationMonth" type="hidden" />
          <input id="mp-cardExpirationYear" data-checkout="cardExpirationYear" type="hidden" />
          <input id="mp-docType" data-checkout="docType" type="hidden" defaultValue="Otro" />
          <input id="mp-docNumber" data-checkout="docNumber" type="hidden" defaultValue={docNumberRef.current} />
        </form>

        {/* Hidden Stripe v2 form */}
        <form ref={stripeFormRef} name="stripeForm" style={{ display: 'none' }}>
          <input id="sv2-name" data-stripe="name" type="hidden" />
          <input id="sv2-number" data-stripe="number" type="hidden" />
          <input id="sv2-exp-month" data-stripe="exp-month" type="hidden" />
          <input id="sv2-exp-year" data-stripe="exp-year" type="hidden" />
          <input id="sv2-cvc" data-stripe="cvc" type="hidden" />
        </form>

        <FeeBreakdownDisplay feeBreakdown={feeBreakdown} displayCurrency={displayCurrency} />

        <PaymentAcceptances
          guaranteeType={feeBreakdown.guaranteeType}
          feeAmount={feeAmount}
          displayCurrency={displayCurrency}
          serviceFee={feeBreakdown.serviceFee}
          cancellationHours={cancellationHours}
          isWaitingList={booking.flowType === 'waiting_list'}
          onAllAcceptedChange={setAllAccepted}
        />

        {error && (
          <div
            role="alert"
            className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Commit-host error from useReservationCommit (post-tokenization
            failures, 3DS rejects, backend bookings blocks). Null when
            this form isn't the commit host. */}
        {commitError && (
          <div
            role="alert"
            data-testid="payment-commit-error"
            className="flex items-center gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{commitError}</span>
          </div>
        )}
      </div>

      <div className="w-full sm:px-6 mt-8">
        <Button
          data-testid="payment-submit"
          {...verifyAttrs("MercadoPagoPaymentSubmit", { action: "confirm-booking" })}
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
          onClick={handleSubmit}
          disabled={!sdksReady || !allAccepted || isProcessing || isSubmitting || requireReload}
        >
          {requireReload
            ? tErrors('PAYMENT_3DS_RELOAD_TO_RETRY')
            : isProcessing
              ? // Tokenization in flight (Stripe v2 fallback + MP token).
                t('PAYMENT_PROCESSING')
              : // T30 D1: copy reflects what the user authorizes regardless
                // of whether the click commits or advances. See the matching
                // comment in StripePaymentForm.
                t(commitCtaKey({
                  hostStep: 'guarantee',
                  // See verifyAttrs comment above. Deposit-only "pay
                  // now" semantics; no_show is a hold and uses the
                  // simpler "Reservar" copy.
                  hasFee: feeBreakdown.guaranteeType === 'deposit' && feeBreakdown.total > 0,
                  isSubmitting,
                  hasError: !!commitError,
                  isWaitingList: booking.flowType === 'waiting_list',
                }), {
                  amount: `${displayCurrency} ${feeBreakdown.total.toLocaleString()}`,
                })}
        </Button>
      </div>

      {/* T30 D2: 3DS overlay. Inert unless useReservationCommit's commit
          path triggers a 3DS challenge mid-flight. */}
      <ThreeDSChallengeOverlay open={isHandling3ds} />
    </BookingPageLayout>
  );
}
